import { letter as L, mark as M, cjkThai } from "./unicodePatterns.js";

/**
 * Matches the letter/number runs that spannify() wraps in spans (no
 * punctuation). Shared with utils.splitPhraseIntoSpanTokens() so saved phrases
 * are tokenized identically to the rendered spans.
 */
export const spanWordRegExp = () => new RegExp(`([‘’${L}${M}]+|[0-9]+)`, "gu");

/**
 * Wraps each word or character in the input HTML with <span> tags containing position data.
 * This enables individual words/characters to be clickable for translation in the Readlang reader.
 *
 * The function intelligently handles different writing systems:
 * - For alphabetic languages (Latin, Cyrillic, etc.): wraps entire words
 * - For CJK languages (Chinese/Japanese/Korean) and Thai: wraps individual characters
 * - For Catalan: splits contractions on apostrophes into separate spans
 *
 * HTML tags and entities are preserved and not wrapped. Content can optionally be excluded
 * from wrapping by placing it in <strong> tags (when ignoreStrong option is used).
 *
 * @param {string} inputHtml - The HTML content to process
 * @param {{
 *   startPosition?: number,
 *   onEachSpan?: (position: number, offset: number, cumulativeOffset: number) => void,
 *   language?: string,
 *   ignoreStrong?: boolean,
 *   includePunctuation?: boolean,
 * }} options
 *   - startPosition: Starting position number for data-p attributes (default: 0)
 *   - onEachSpan: Callback invoked for each span with position, offset, and cumulative offset
 *   - language: Language code for special handling (e.g., "ca" for Catalan)
 *   - ignoreStrong: If true, skip wrapping content inside <strong> tags
 *   - includePunctuation: If true, include punctuation marks as separate spans
 *
 * @returns {{
 *   html: string;
 *   totalSpans: number;
 * }}
 *   - html: The processed HTML with span wrappers
 *   - totalSpans: Total number of spans added (final position value)
 */
export default function (inputHtml, options) {
  /** Warning: this also includes punctuation */
  const wordRegExp = options.includePunctuation
    ? new RegExp(
        `([\u2018\u2019${L}${M}]+|[0-9]+|[\\xAB\\xBB\u201C\u201D\u201E.,?!\\xA1\\xBF])`,
        "gu"
      )
    : spanWordRegExp();

  let cumulativeOffset = 0;
  let position = options.startPosition || 0;

  const hanRegExp = new RegExp(cjkThai, "u");
  const combiningMark = new RegExp(`[${M}]`, "u");
  let ignoring = false;

  const html = inputHtml.replace(
    wordRegExp,
    (_wholeMatch, group1, offset, wholeString) => {
      if (wholeString[offset - 1] === "<") {
        if (group1 === "strong") {
          ignoring = true;
        }
        return group1;
      } else if (
        wholeString[offset - 2] === "<" &&
        wholeString[offset - 1] === "/"
      ) {
        if (group1 === "strong") {
          ignoring = false;
        }
        return group1;
      } else if (wholeString[offset - 1] === "&") {
        return group1;
      } else if (
        wholeString[offset - 2] === "<" &&
        wholeString[offset - 1] === "h"
      ) {
        // Ignore <h1> <h2> etc.
        return group1;
      } else if (
        wholeString[offset - 3] === "<" &&
        wholeString[offset - 2] === "/" &&
        wholeString[offset - 1] === "h"
      ) {
        // Ignore </h1> </h2> etc.
        return group1;
      }

      if (options.ignoreStrong && ignoring) {
        return group1;
      }

      if (options.onEachSpan) {
        options.onEachSpan(position, offset, cumulativeOffset);
      }

      let startTag;
      let endTag;

      if (hanRegExp.test(group1)) {
        let result = "";
        for (let i = 0; i < group1.length; i++) {
          startTag = "<span data-p=" + position++ + ">";
          endTag = "</span>";

          result += startTag + group1[i];
          while (i < group1.length - 1 && combiningMark.test(group1[i + 1])) {
            i++;
            result += group1[i];
          }

          result += endTag;
          cumulativeOffset += startTag.length + endTag.length;
        }
        return result;
      }

      startTag = "<span data-p=" + position++ + ">";
      endTag = "</span>";

      if (options.language === "ca") {
        const delimiterResult = /['`’]/.exec(group1);

        if (delimiterResult && delimiterResult.length > 0) {
          const delimiter = delimiterResult[0];

          const split = group1.split(delimiter);

          if (split[0].length <= split[1].length) {
            split[0] += delimiter;
          } else {
            split[1] = delimiter + split[1];
          }

          // TODO: is this messing up the cumulativeOffset??
          startTag = "<span data-p=" + position++ + ">";
          group1 = split[0] + endTag + startTag + split[1];

          cumulativeOffset += startTag.length + endTag.length;
          startTag = "<span data-p=" + (position - 2) + ">";
        }
      }

      cumulativeOffset += startTag.length + endTag.length;
      return startTag + group1 + endTag;
    }
  );

  return {
    html: html,
    totalSpans: position,
  };
}
